import { createReducer, on } from "@ngrx/store";
import { checkRecipientTypeDict, Constants, distributionReasons, payeeType, RothConversion, RothWarnings, StwProcessCode, transactionTypes } from "../../shared/utilities/Constants";
import { CheckActions, ConfirmationPageActions, ReviewPageActions, TransactionFormActions, StandingAuthorizationsActions } from "../actions";
import { CheckInfo } from "../models/check/CheckInfo";
import { CheckData } from "../models/check/CheckData";
import { DistributionReason } from "../models/internal-transfer/DistributionReason";
import { CodeAndDescription } from "../models/shared/CodeAndDescription";
import { AccountInfoResponse, Classifications } from "../../account-info-header/models";
import { TaxAmountAndPercentageFormatter } from '../../shared/utilities/TaxAmountAndPercentageFormatter';
import { DateTime } from "luxon";
import { CheckInquiryResponse } from "../models/check/CheckInquiryResponse";
import { ViewPageActions } from "../../transaction-view/actions";
import { ChecksExtraPrompts, CheckTransactionDetails } from "../models/form/TransactionForm";
import { AccountInfoActions } from "../../account-info-header/actions";
import { EAuthModelCustomer } from '../models/internal-transfer/EAuthModelCustomer';
import { RecipientAddressDetails } from "../models/check/CheckVerifyRequest";
import { InitReleaseActions } from "../../initiate-and-release/actions";
import { StandingAuthCheckActions } from "../../standing-authorizations/actions";
import { inquiryCallSuccessful } from '../actions/ach.actions';
import { DraftListAttributes } from "../../initiate-and-release/Models/Drafts/DraftResponse";
import { CheckExtraPrompts } from "../../standing-authorizations/models/standingAuthorizationForm";

export const checkInfoKey = 'checkInfo';

export interface State extends CheckInfo { }

export const initialCheckInfoState: State = {
  nickName: '',
  payIncome: { key: '', value: '' },
  payee: [],
  caseId: '',
  iraFields: {
    isIraTransaction: false,
    isAccountTaxable: false,
    hasActiveTaxElections: false,
    fedTaxPercent: '',
    fedTaxAmount: '',
    isWithholdFedTax: false,
    stateTaxPercent: '',
    stateTaxAmount: '',
    stateTaxAttentionRequired: false,
    isWithholdStateTax: false,
    stateTypeIndicator: '',
    stwProcessCode: '',
    stateTaxAttentionMessage: '',
    isCashieringIRAAgreementOpened: false,
    rmdAnnualAmount: '',
    rmdAnnualPaidYearToDateAmount: '',
    isRmdToDisplay: false,
    taxValueOriginMessage: '',
    distributionReason: {
      code: '',
      subCode: '',
      description: '',
      isTaxExempt: false
    },
    distributionReasonMap: [],
    nraAccountIndicator: false
  },
  withdrawalReason: '',
  recurringFields: {
    isRecurringTransaction: false,
    frequencyList: [],
    payIncomeMap: [],
    frequency: {
      code: '',
      description: ''
    },
    payIncome: {
      code: '',
      description: ''
    },
    endDate: '',
    nextProcessDate: '',
    secondProcessDate: ''
  },
  loading: false,
  hasError: false,
  error: '',
  inputCallFinished: false,
  inputCallSucceeded: false,
  verifyCallFinished: false,
  inquiryCallFinished: false,
  standingAuthCallSucceeded: false,
  initReleaseByDraftIdSucceeded: false,
  defaultPayee: [],
  defaultRecipient: [],
  checkFeeDetails: {
    defaultFee: 0,
    qualFee: 0,
    feeWaiverCode: ''
  },
  checkReasonMap: [],
  recipient: {
    addressLine1: '',
    addressLine2: '',
    addressLine3: '',
    city: '',
    regionName: '',
    stateName: '',
    stateCode: '',
    countryCode: '',
    countryName: '',
    zipCode: '',
    zipSuffCode: ''
  },
  firmDetails: {
    customerId: 0,
    name: "",
    id: 0,
    streetAddr: "",
    streetAddr1: "",
    streetAddr2: "",
    streetAddr3: "",
    streetAddr4: "",
    city: "",
    state: "",
    zip: "",
    requestorFirstName: "",
    requestorLastName: "",
    firmStreetAddr1: "",
    firmStreetAddr2: ""
  },
  isAlternateInvestmentUsage: false,
  alternateInvestmentText: '',
  isFirstPartyModified: false,
  payeeDetails: {
    payeeName1: '',
    payeeName2: '',
    payeeName3: '',
    customerId1: 0,
    customerId2: 0,
    customerId3: 0,
  },
  useConstructedPayee: false,
  memo: '',
  requestType: '',
  accountPlatformCode: '',
  isNonNaState: false,
  processDate: '',
  checkReason: '',
  deliveryAddress: [],
  checkFee: 0,
  anyAccountHolderIneligible: false,
  accountHolderIneligibleLog: false,
  useEauthCheckDisableLog: false,
  cashieringIsCustAuthRequired: false,
  acknowledgementArtifactContent: '',
  status: { code: '', description: '' },
  isThirdParty: false,
  messageInfo: {
    errorList: [],
    warningList: [],
    atsSystemErrors: []
  },
  requestedDate: "",
  requestedTime: "",
  amount: '',
  totalWithdrawalAmount: '',
  fedTaxAmountFormatted: '',
  inputIraFields: {
    stateTaxAmountFormatted: '',
    stateTaxPercent: '',
    fedTaxPercent: '',
  },
  stateTaxAmountFormatted: '',
  iraAccount: false,
  pendReasons: [],
  stateCd: '',
  distributionReasonsList: [],
  fedTaxAmount: '',
  fedTaxRate: '',
  stateTaxAmount: '',
  stateTaxRate: '',
  stwProcessCode: '',
  stateTypeIndicator: '',
  nraAccountIndicator: false,
  stateList: [],
  frequencyList: [],
  countryList: [],
  availableCashMmf: '',
  availableCashMargin: '',
  shouldShowStateTax: false,
  distributionReason: {
    code: '',
    subCode: '',
    description: '',
    isTaxExempt: false,
  },
  displayClientAuthorizationSection: false,
  eAuthModel: {
    isTransactionFromSLOA: false,
    eauthSelected: false,
    isTransactionFromSLOAOrSLOA: false,
    numberOfAuthRequired: 0,
    eauthEligibilityServiceSLOAError: false,
    eauthEligibilityServiceDown: false,
    eauthEligibilityServiceSLOAErrorMsg: '',
    isOrgAccount: false,
    accountRestricted: false,
    orgOrEduSavingsAccount: false,
    custAcctAuthorizationsListEmpty: false,
    eauthInd: false,
    eauthSLOAInd: false,
    allAccountHolderIneligible: false,
    numOfIneligibleCust: 0,
    eauthEligibilityServiceDownErrorMsg: '',
    custAcctAuthorizationsList: {
      allAccountHoldersIneligible: false,
      numOfIneligibleCust: 0,
      numOfNotWebEnabledCust: 0,
      totalNumOfAccountHolders: 0,
      eauthInd: false,
      eAuthCustomerList: [],
    },
    custAcctSLOAAuthorizationsList: {
      allAccountHoldersIneligible: false,
      numOfIneligibleCust: 0,
      numOfNotWebEnabledCust: 0,
      totalNumOfAccountHolders: 0,
      eauthInd: false,
      eAuthCustomerList: [],
    },
    transactionType: '',
    submitButtonsDisable: '',
    txnType: '',
  },
  clientAuthorizationNeeded: false,
  pendReasonsListSet: [],
  isRmdEligible: false,
  rmdAnnualRemainingAmount: '',
  mailAddressDetails: [],
  checkPayeeTypeCode: '',
  mailAddressTypeCode: '',
  cashieringRequestId: 0,
  cashieringRequestSecondId: 0,
  countryCode: '',
  isWithholdFedTax: false,
  isWithholdStateTax: false,
  cashieringCheckInputDetails: {
    secondProcessDate: '',
    nextProcessDate: '',
    frequencyCode: '',
    isRecurringTransaction: false,
    cashieringAddressDetails: {
      recipientAddressType: ''
    }
  },
  serviceError: '',
  isPendingForCTWE: false,
  asOfDate: '',
  depositAmount: '',
  fundsHeld: [],
  contributionType: '',
  isUseOrCreateStandingAuth: false,
  isSubmitAndCreate: false,
  cashieringIraAgreementText: '',
  isBsloaTransaction: false,
  checkNumber: 0,
  assignToUserId: '',
  memoLine1CustomerId: 0,
  authResponse: {
    draftId: '',
    pendForRelease: false,
    pendForReleaseReason: '',
    canRelease: false
  },
  revalidation: {
    status: '',
    errors: [],
    fieldsUpdated: []
  },
  feeDetail: {
    chargedFee: 0,
    deliveryFee: 0,
    isFeePaidByFirm: false,
    waivedAmount: 0
  },
  regShortName: '',
}

export const checkReducer = createReducer(
  initialCheckInfoState,
  on(CheckActions.callOneTimeInput,
    CheckActions.callRecurringInput, (state) => {
      return {
        ...state,
        loading: true,
        inputCallFinished: false,
        inputCallSucceeded: false
      }
    }),
  on(CheckActions.inputCallSuccessful, (state, response) => {
    const { checkInputResponse } = response
    return {
      ...state,
      loading: false,
      error: '',
      hasError: false,
      inputCallFinished: true,
      inputCallSucceeded: true,
      memo: checkInputResponse.memo,
      isThirdParty: checkInputResponse.isThirdParty,
      defaultPayee: checkInputResponse?.cashieringCheckInputDetails?.cashieringPayeeDetails?.defaultPayee,
      defaultRecipient: checkInputResponse?.cashieringCheckInputDetails?.cashieringAddressDetails?.defaultRecipient,
      checkFeeDetails: checkInputResponse?.checkFeeDetails,
      checkReasonMap: mapCheckReasonsToList(checkInputResponse?.withdrawalReasonMap),
      recipient: checkInputResponse?.cashieringCheckInputDetails?.cashieringAddressDetails?.addressDetails,
      isAlternateInvestmentUsage: checkInputResponse?.isAlternateInvestmentUsage,
      alternateInvestmentText: checkInputResponse?.alternateInvestmentText,
      isFirstPartyModified: checkInputResponse?.isFirstPartyModified,
      payeeDetails: checkInputResponse?.cashieringCheckInputDetails?.cashieringPayeeDetails?.payeeDetails,
      useConstructedPayee: checkInputResponse?.cashieringCheckInputDetails?.cashieringPayeeDetails?.useConstructedPayee,
      requestType: checkInputResponse.type,
      accountPlatformCode: checkInputResponse?.cashieringCheckInputDetails?.cashieringAccountRefDetails?.accountPlatformCode,
      isNonNaState: checkInputResponse?.cashieringCheckInputDetails?.cashieringAccountRefDetails?.isNonNaState,
      processDate: `${checkInputResponse.processDateMM}-${checkInputResponse.processDateDD}-${checkInputResponse.processDateYYYY}`,
      nextProcessDate: checkInputResponse.cashieringCheckInputDetails.nextProcessDate,
      secondProcessDate: `${checkInputResponse.secondProcessDateMM}-${checkInputResponse.secondProcessDateDD}-${checkInputResponse.secondProcessDateYYYY}`,
      endDate: `${checkInputResponse.endDateMM}-${checkInputResponse.endDateDD}-${checkInputResponse.endDateYYYY}`,
      checkFee: checkInputResponse.checkFeeDetails.defaultFee - checkInputResponse.checkFeeDetails.qualFee,
      stateCd: checkInputResponse?.cashieringCheckInputDetails?.cashieringAccountRefDetails?.stateCd,
      distributionReasonsList: checkInputResponse.distributionReasonsList,
      distributionReason: mapCheckDistributionReasonList(checkInputResponse),
      distributionReasonCode: checkInputResponse?.distributionReasonCode ?? '',
      stateTaxAmount: checkInputResponse?.cashieringCheckInputDetails?.cashieringCheckTransaction?.iraTxn?.stateTaxAmount.toString(),
      stateTaxRate: (checkInputResponse?.cashieringCheckInputDetails?.cashieringCheckTransaction?.iraTxn?.stateTaxRate * 100).toString(),
      fedTaxAmount: checkInputResponse?.cashieringCheckInputDetails?.cashieringCheckTransaction?.iraTxn?.fedTaxAmount.toString(),
      fedTaxRate: (checkInputResponse?.cashieringCheckInputDetails?.cashieringCheckTransaction?.iraTxn?.fedTaxRate * 100).toString(),
      stwProcessCode: checkInputResponse?.cashieringCheckInputDetails?.cashieringAccountRefDetails?.stwProcessCode as StwProcessCode,
      stateTypeIndicator: checkInputResponse?.cashieringCheckInputDetails?.cashieringCheckTransaction?.stateTypeIndicator,
      nraAccountIndicator: checkInputResponse?.cashieringCheckInputDetails?.cashieringCheckTransaction?.nraAccountIndicator,
      stateList: checkInputResponse.stateList,
      frequencyList: checkInputResponse.frequencyList,
      countryList: checkInputResponse.countryList,
      fedTaxAmountFormatted: checkInputResponse?.cashieringCheckInputDetails?.cashieringCheckTransaction?.fedTaxAmountFormatted,
      inputIraFields: {
        stateTaxAmountFormatted: checkInputResponse?.cashieringCheckInputDetails?.cashieringCheckTransaction?.stateTaxAmountFormatted,
        stateTaxPercent: (checkInputResponse?.cashieringCheckInputDetails?.cashieringCheckTransaction?.stateTaxAmountFormatted),
        fedTaxPercent: (checkInputResponse?.cashieringCheckInputDetails?.cashieringCheckTransaction?.fedTaxAmountFormatted),
      },
      stateTaxAmountFormatted: checkInputResponse?.cashieringCheckInputDetails?.cashieringCheckTransaction?.stateTaxAmountFormatted,
      totalWithdrawalAmount: checkInputResponse.totalWithdrawalAmount,
      iraAccount: checkInputResponse.iraaccount,
      availableCashMmf: checkInputResponse?.cashieringCheckInputDetails?.cashieringAccountRefDetails?.cashAvailableCashMmf,
      availableCashMargin: checkInputResponse?.cashieringCheckInputDetails?.cashieringAccountRefDetails?.cashAvailableOnMargin,
      firmDetails: checkInputResponse?.firmDetails,
      shouldShowStateTax: hasStateTax(
        checkInputResponse?.cashieringCheckInputDetails?.cashieringCheckTransaction?.nraAccountIndicator,
        checkInputResponse?.cashieringCheckInputDetails?.cashieringAccountRefDetails?.stwProcessCode,
        checkInputResponse?.cashieringCheckInputDetails?.cashieringCheckTransaction?.stateTaxAmountFormatted
        || (checkInputResponse?.cashieringCheckInputDetails?.cashieringAccountRefDetails?.stwProcessCode === 'N'
          ? checkInputResponse?.cashieringCheckInputDetails?.cashieringAccountRefDetails?.defaultStateTaxRate?.toString()
          : undefined)
      ),
      isRmdEligible: checkInputResponse?.cashieringCheckInputDetails?.cashieringAccountRefDetails?.isRmdEligible,
      rmdAnnualRemainingAmount: checkInputResponse?.cashieringCheckInputDetails?.cashieringAccountRefDetails?.rmdAnnualRemainingAmount,
      mailAddressDetails: checkInputResponse?.cashieringCheckInputDetails?.cashieringAddressDetails?.mailAddressDetails,
      mailAddressTypeCode: checkInputResponse?.cashieringCheckInputDetails?.cashieringAddressDetails?.mailAddressTypeCode,
      checkPayeeTypeCode: checkInputResponse?.cashieringCheckInputDetails?.cashieringPayeeDetails?.checkPayeeTypeCode,
      cashieringRequestId: checkInputResponse?.cashieringReqId,
      cashieringRequestSecondId: checkInputResponse?.cashieringReqSecondaryId,
      countryCode: checkInputResponse?.cashieringCheckInputDetails?.cashieringAddressDetails?.addressDetails?.countryCode,
      serviceError: '',
      isUseOrCreateStandingAuth: checkInputResponse.cashieringCheckInputDetails.isTransactionFromSLOAOrSLOA,
      cashieringIraAgreementText: checkInputResponse.cashieringIraAgreementText,
      assignToUserId: checkInputResponse.assignToUserId,
      memoLine1CustomerId: checkInputResponse.memoLine1CustomerId,
      regShortName: checkInputResponse.cashieringCheckInputDetails?.accountDisplayRecord?.regShortName
    };
  }),
  on(CheckActions.inputCallFailed, (state, err) => {
    return {
      ...state,
      loading: false,
      error: err.message,
      hasError: true,
      inputCallFinished: true,
      inputCallSucceeded: false
    }
  }),
  on(CheckActions.inputCallSuccessfulSilent, (state, response) => {
    const { checkInputResponse } = response
    // Only update essential data like country list, no loading states or other side effects
    return {
      ...state,
      countryList: checkInputResponse.countryList,
      stateList: checkInputResponse.stateList,
      frequencyList: checkInputResponse.frequencyList,
      error: '',
      hasError: false
    };
  }),
  on(StandingAuthCheckActions.callCheckInquiry, (state, err) => {
    return {
      ...state,
      loading: true,
      inquiryCallfinished: false,
      hasError: false,
    }
  }),
  on(StandingAuthCheckActions.inquiryCallSuccessful, (state, err) => {
    return {
      ...state,
      loading: false,
      inquiryCallfinished: true,
      hasError: false,
    }
  }),
  on(StandingAuthCheckActions.inquiryCallFailed, (state, err) => {
    return {
      ...state,
      loading: false,
      inquiryCallfinished: true,
      inquiryCallSuccessful: false,
      hasError: true,
      error: err.message,
    }
  }),
  on(TransactionFormActions.distributionReasonChanged, (state, response) => {
    if (response.transactionType === transactionTypes.check) {
      return {
        ...state,
        distributionReason: response.distributionReason,
      };
    }
    return { ...state };
  }),
  on(TransactionFormActions.loadChecksReviewPageWithoutVerifyCall, (state, form) => {
    const details = form.transactionDetails as CheckTransactionDetails;
    let checkReason = state.checkReasonMap.find(reason => reason.code === details.checkReason!)?.description ?? '';
    return {
      ...state,
      loading: false,
      hasError: false,
      error: '',
      verifyCallFinished: true,
      serviceError: '',
      payee: getPayeeNameForNonVerifyCallFlow(details),
      deliveryAddress: getDeliveryAddressForNonVerifyCallFlow(details, state.countryList),
      requestedDate: DateTime.now().toFormat('MM/dd/yyyy'),
      processDate: details.processDate ?? '',
      amount: details.amount ?? '',
      checkReason: checkReason,
      memo: details.checkMemo ?? ''
    }
  }),
  on(TransactionFormActions.continueClickTotalDist, (state, { transactionForm }) => {
    const details = transactionForm.transactionDetails as CheckTransactionDetails;
    let extraPrompts = transactionForm.extraPrompts as CheckExtraPrompts
    if (transactionForm.transactionType === transactionTypes.check) {
      const extraPrompts = transactionForm.extraPrompts as ChecksExtraPrompts;
      const isOnetimeTotalDist = extraPrompts?.frequency === 'onetimeTotalDist';
      return {
        ...state,
        totalWithdrawalAmount: Constants.checksTotalWithdrawalAmountText,
        payee:  getPayeeNameForNonVerifyCallFlow(details),
        processDate: details.processDate ?? '',
        memo: details.checkMemo ?? '',
        fedTaxRate: isOnetimeTotalDist && (details?.taxWithholdingsFormGroup?.distributionReason === distributionReasons.qcd || details?.taxWithholdingsFormGroup?.distributionReason === distributionReasons.qualified || details?.taxWithholdingsFormGroup?.distributionReason === distributionReasons.qcdY4) ? '0' : state.fedTaxRate,
        stateTaxRate: isOnetimeTotalDist && (details?.taxWithholdingsFormGroup?.distributionReason === distributionReasons.qcd || details?.taxWithholdingsFormGroup?.distributionReason === distributionReasons.qualified || details?.taxWithholdingsFormGroup?.distributionReason === distributionReasons.qcdY4) ? '0' : state.stateTaxRate,
        fedTaxAmountFormatted: isOnetimeTotalDist && (details?.taxWithholdingsFormGroup?.distributionReason === distributionReasons.qcd || details?.taxWithholdingsFormGroup?.distributionReason === distributionReasons.qualified || details?.taxWithholdingsFormGroup?.distributionReason === distributionReasons.qcdY4) ? '0' : state.fedTaxAmountFormatted,
        stateTaxAmountFormatted: isOnetimeTotalDist && (details?.taxWithholdingsFormGroup?.distributionReason === distributionReasons.qcd || details?.taxWithholdingsFormGroup?.distributionReason === distributionReasons.qualified || details?.taxWithholdingsFormGroup?.distributionReason === distributionReasons.qcdY4) ? '0' : state.stateTaxAmountFormatted,        
        deliveryAddress: [
          details.addresseeName ?? '', details.addresseeNameCont ?? '', details.addresseeNameCont2 ?? '',
          details.streetAddress ?? '', details.streetAddressCont ?? '',
          details.recipient === checkRecipientTypeDict["addressforeign"] ?
            (details.city ?? '') + ", " + (details.stateProvince ?? '') + " " + (details.postalCode ?? '')
            : (details.city ?? '') + ", " + (details.state ?? '') + " " + (details.zipCode ?? ''),
          details.country ?? ''
        ],
        isThirdParty: details.payeeType === payeeType.thirdParty
      };
    }
    return { ...state };
  }),
  on(TransactionFormActions.callOnetimeCheckVerify,
    TransactionFormActions.callRecurringCheckVerify,
    ReviewPageActions.editClicked,
    TransactionFormActions.transactionTypeChanged, (state) => {
      return {
        ...state,
        verifyCallFinished: false,
        isSubmitAndCreate: false,
        serviceError: '',
      }
    }),
  on(
    TransactionFormActions.editLoadComplete,
    TransactionFormActions.editLoadCompleteForEditPage,
    (state) => {
      return {
        ...state,
        inputCallFinished: true
      };
    }),
  on(CheckActions.verifyCallSuccessful, (state, response) => {
    const { checkVerifyResponse } = response;

    const fedTax = TaxAmountAndPercentageFormatter(
      checkVerifyResponse.fedTaxAmountFormatted
    );
    const stateTax = TaxAmountAndPercentageFormatter(
      checkVerifyResponse.stateTaxAmountFormatted
    );

    return {
      ...state,
      loading: false,
      hasError: false,
      error: '',
      verifyCallFinished: true,
      payee: checkVerifyResponse.payee,
      checkReason: state.initReleaseByDraftIdSucceeded ? state.checkReason ?? '' : checkVerifyResponse.withdrawalReasonDescription,
      deliveryAddress: state.initReleaseByDraftIdSucceeded && state.deliveryAddress.filter(item => item != "").length != 0 ? state.deliveryAddress : checkVerifyResponse.recipient,
      memo: checkVerifyResponse.memo,
      anyAccountHolderIneligible: checkVerifyResponse.anyAccountHolderIneligible,
      accountHolderIneligibleLog: checkVerifyResponse.accountHolderIneligibleLog,
      useEauthCheckDisableLog: checkVerifyResponse.useEAuthCheckDisableLog,
      cashieringIsCustAuthRequired: checkVerifyResponse.cashieringIsCustAuthRequired,
      messageInfo: checkVerifyResponse.messageInfo,
      warningList: checkVerifyResponse.messageInfo.warningList,
      amount: checkVerifyResponse.amount,
      processDate: `${checkVerifyResponse.processDateMM}-${checkVerifyResponse.processDateDD}-${checkVerifyResponse.processDateYYYY}`,
      nextProcessDate: checkVerifyResponse.cashieringCheckInputDetails.nextProcessDate,
      cashieringCheckInputDetails: {
        ...state.cashieringCheckInputDetails,
        ...checkVerifyResponse.cashieringCheckInputDetails
      },
      fedTaxAmountFormatted: checkVerifyResponse.fedTaxAmountFormatted,
      stateTaxAmountFormatted: checkVerifyResponse.stateTaxAmountFormatted,
      totalWithdrawalAmount: checkVerifyResponse.totalWithdrawalAmount,
      iraAccount: checkVerifyResponse.iraaccount,
      fedTaxRate: fedTax.percentage,
      stateTaxRate: stateTax.percentage,
      displayClientAuthorizationSection: checkVerifyResponse.displayClientAuthorizationSection,
      eAuthModel: {
        ...checkVerifyResponse.eAuthModel,
        custAcctSLOAAuthorizationsList: checkVerifyResponse.eAuthModel.custAcctAuthorizationsSLOAList
      },
      clientAuthorizationNeeded: checkVerifyResponse.clientAuthorizationNeeded,
      pendReasonsListSet: checkVerifyResponse.pendReasonsListSet,
      isWithholdFedTax: Number.parseFloat(fedTax.percentage) !== 0,
      isWithholdStateTax: Number.parseFloat(stateTax.percentage) !== 0,
      serviceError: '',
      payIncome: checkVerifyResponse.payIncome,
      isPendingForCTWE: checkVerifyResponse.isPendingForCTWE,
      isSubmitAndCreate: false,
      acknowledgementConsentText: checkVerifyResponse.acknowledgementArtifactContent,
      isThirdParty: checkVerifyResponse.isThirdParty,
      authResponse: checkVerifyResponse.authorizeAttributeResponse,
      revalidation: {
        ...checkVerifyResponse.revalidation,
        status: checkVerifyResponse.revalidation?.status,
        errors: checkVerifyResponse.revalidation?.errors,
        fieldsUpdated: checkVerifyResponse.revalidation?.fieldsUpdated
      },
      shouldShowStateTax: hasStateTax(
        state?.nraAccountIndicator,
        state?.stwProcessCode,
        checkVerifyResponse.stateTaxAmountFormatted
        || (state?.stwProcessCode === 'N' ? checkVerifyResponse.stateTaxRate?.toString() : undefined)
      ),
      checkFee: state.initReleaseByDraftIdSucceeded ? state.feeDetail?.deliveryFee ?? 0 : state.checkFee
    }
  }),
  on(CheckActions.verifyCallFailed, (state, err) => {
    return {
      ...state,
      loading: false,
      hasError: true,
      error: err.message,
      verifyCallFinished: false
    }
  }),
  on(CheckActions.verifyCallFinishedWithError, (state, { checkVerifyResponse }) => {
    const messageInfo = checkVerifyResponse?.messageInfo;
    const errorObject = messageInfo?.atsSystemErrors?.[0] || messageInfo?.errorList?.[0];
    return {
      ...state,
      serviceError: errorObject?.messageText ?? Constants.genericServiceError,
      revalidation: {
        ...checkVerifyResponse.revalidation,
        status: checkVerifyResponse.revalidation?.status,
        errors: checkVerifyResponse.revalidation?.errors,
        fieldsUpdated: checkVerifyResponse.revalidation?.fieldsUpdated
      }
    }
  }),
  on(CheckActions.nsfClientSideError, (state) => {
    return {
      ...state,
      serviceError: 'INSUFFICIENT FUNDS FOR THIS REQUEST',
      hasError: true
    }
  }),
  on(CheckActions.callOnetimeAcknowledge,
    CheckActions.callRecurringAcknowledge, (state) => {
      return {
        ...state,
        loading: true
      }
    }),
  on(CheckActions.acknowledgeCallSuccessful, (state, { type, ...action }) => {
    const { checkAcknowledgeResponse } = action;
    return {
      ...state,
      loading: false,
      pendReasons: checkAcknowledgeResponse.pendReasonsListSet,
      status: { code: checkAcknowledgeResponse.statusCode, description: checkAcknowledgeResponse.statusDescription },
      messageInfo: checkAcknowledgeResponse.messageInfo,
      requestedDate: checkAcknowledgeResponse.requestedDate,
      requestedTime: checkAcknowledgeResponse.requestedTime
    }
  }),
  on(CheckActions.acknowledgeCallFailed, (state, err) => {
    return {
      ...state,
      loading: false,
      hasError: true,
      error: err.message,
      acknowledgeCallFinished: true,
      errorList: err.error?.errors ?? []
    }
  }),
  on(CheckActions.inputCallFinishedWithError, (state, response) => {
    return {
      ...state,
      toAccountState: {
        loading: false,
        error: '',
        hasError: false
      },
    }
  }),
  on(ConfirmationPageActions.createAnotherRequestClicked,
    ConfirmationPageActions.viewTransactionStatusClicked,
    CheckActions.extraPromptsChanged,
    ReviewPageActions.cancelClicked,
    TransactionFormActions.cancelClicked,
    InitReleaseActions.getInitReleaseList, () => {
      return initialCheckInfoState;
    }),
  on(StandingAuthorizationsActions.loadSloas, (state, _) => {
    return {
      ...state,
      inputCallFinished: false,
      inputCallSucceeded: false,
      standingAuthCallSucceeded: false,
    };
  }),
  on(
    StandingAuthorizationsActions.loadSloasSuccess,
    (state, _) => {
      return {
        ...state,
        standingAuthCallSucceeded: true,
      };
    }
  ),
  on(
    StandingAuthorizationsActions.loadSloasFailure,
    (state, _) => {
      return {
        ...state,
        standingAuthCallSucceeded: false,
        inputCallSucceeded: false
      };
    }
  ),
  on(
    TransactionFormActions.editLoadComplete,
    TransactionFormActions.editLoadCompleteForEditPage,
    (state) => {
      return {
        ...state,
        inputCallFinished: true,
        standingAuthorizationsCallFinished: true,
      };
    }),
  on(CheckActions.incomingInquiryCallSuccessful, (state, { checkInquiryResponse }) => {
    return {
      ...state,
      asOfDate: checkInquiryResponse.asOfDate,
      depositAmount: checkInquiryResponse.cashieringCheckInputDetails.formattedAmount,
      fundsHeld: mapFundsHeld(checkInquiryResponse.fundsHeld),
      processDate: checkInquiryResponse.processDate,
      contributionType: checkInquiryResponse.iraContributionType ? `${checkInquiryResponse.cashieringCheckInputDetails.cashieringAccountRefDetails.processDateYearFormat} ${checkInquiryResponse.iraContributionType}` : '',
      iraAccount: checkInquiryResponse.iraaccount,
      checkNumber: checkInquiryResponse.checkNumber,
      isRmdEligible: checkInquiryResponse?.cashieringCheckInputDetails?.cashieringAccountRefDetails?.isRmdEligible,
    }
  }),
  on(AccountInfoActions.loadAccountInfoSuccess, (state, info) => {
    return {
      ...state,
      eAuthModel: {
        ...state.eAuthModel,
        custAcctSLOAAuthorizationsList: {
          ...state.eAuthModel.custAcctSLOAAuthorizationsList,
          eAuthCustomerList: UpdateEauthCustomers(state.eAuthModel.custAcctSLOAAuthorizationsList.eAuthCustomerList, info)
        },
        custAcctAuthorizationsList: {
          ...state.eAuthModel.custAcctAuthorizationsList,
          eAuthCustomerList: UpdateEauthCustomers(state.eAuthModel.custAcctAuthorizationsList.eAuthCustomerList, info)
        }
      }
    }
  }),
  on(InitReleaseActions.initReleaseByDraftIdSuccessful, (state, { response, isSloa, isNraAccount }) => {
    if (isSloa || response.transactionType !== 'check') return state;
    return {
      ...state,
      initReleaseByDraftIdSucceeded: true,
      authResponse: {
        ...state.authResponse,
        draftId: response.draftId
      },
      mailAddressDetails: response.draftObject.Recipientusesloa ?? [],
      defaultRecipient: response.draftObject.sloaRecipient ?? [],
      iraAccount: response.draftObject.IRAAccount ?? false,
      recipient: {
        ...state.recipient,
        addressLine1: response.draftObject.recipient?.addressLine1 ?? '',
        addressLine2: response.draftObject.recipient?.addressLine2 ?? '',
        addressLine3: response.draftObject.recipient?.addressLine3 ?? '',
        city: response.draftObject.recipient?.city ?? '',
        stateName: response.draftObject.recipient?.stateName ?? '',
        stateCode: response.draftObject.recipient?.stateCode ?? '',
        zipCode: response.draftObject.recipient?.zipCode ?? '',
        countryCode: response.draftObject.recipient?.countryCode ?? ''
      },
      payee: [
        response.draftObject.isFirstPartyModified ? response.draftObject.payeeLine1 : `${response.draftObject.payeeDetails?.payeeName1 ? response.draftObject.payeeDetails?.payeeName1 : ''}
        ${response.draftObject.payeeLine2 ?? ''}
        ${response.draftObject.payeeLine3 ?? ''}`
      ],
      mailAddressTypeCode: response.draftObject.mailAddressTypeCode ?? response.draftObject.MailAddressTypeCode ?? '',
      checkPayeeTypeCode: response.draftObject.checkPayeeTypeCode ?? response.draftObject.CheckPayeeTypeCode ?? '',
      cashieringRequestId: response.draftObject.cashieringReqId ?? 0,
      cashieringRequestSecondId: response.draftObject.cashieringSecondReqId ?? 0,
      payeeDetails: {
        ...state.payeeDetails,
        payeeName1: response.draftObject.defaultPayee ? response.draftObject.defaultPayee[0] : '',
        payeeName2: response.draftObject.defaultPayee ? response.draftObject.defaultPayee[1] : '',
        payeeName3: response.draftObject.defaultPayee ? response.draftObject.defaultPayee[2] : '',
        customerId1: response.draftObject.payeeDetails?.customerId1 ?? 0,
        customerId2: response.draftObject.payeeDetails?.customerId2 ?? 0,
        customerId3: response.draftObject.payeeDetails?.customerId3 ?? 0
      },
      defaultPayee: getDefaultPayeeNamesForVerifyCallInRFlow(response),
      deliveryAddress: getConcatenateDeliveryAddress(response),
      processDate: `${response.draftObject.processDateMM}-${response.draftObject.processDateDD}-${response.draftObject.processDateYYYY}`,
      stwProcessCode: response.accountData.stwProcessCode as StwProcessCode ?? '',
      nraAccountIndicator: isNraAccount,
      feeDetail: {
        chargedFee: response.draftObject.feeDetail?.chargedFee ?? 0,
        deliveryFee: response.draftObject.feeDetail?.DeliveryFee ?? 0,
        isFeePaidByFirm: response.draftObject.feeDetail?.IsFeePaidByFirm ?? false,
        waivedAmount: response.draftObject.feeDetail?.WaivedAmount ?? 0
      },
      stateCd: response.accountData.stateCd,
      checkReason: response.draftObject.memoLine2 ?? response.draftObject.MemoLine2 ?? ''
    }
  }),
  on(CheckActions.inquiryCallSuccessful, (state, { checkInquiryResponse }) => {
    const fedTax = TaxAmountAndPercentageFormatter(
      checkInquiryResponse?.fedTaxAmountFormatted
    );
    const stateTax = TaxAmountAndPercentageFormatter(
      checkInquiryResponse?.stateTaxAmountFormatted
    );
    let checkReasonMap = mapCheckReasonsToList(checkInquiryResponse?.withdrawalReasonMap);
    let checkReason = checkReasonMap.find(reason => reason.code === checkInquiryResponse.withdrawalReasonCode!)?.description ?? '';
    const payeeDetails = checkInquiryResponse?.cashieringCheckInputDetails?.cashieringPayeeDetails?.payeeDetails;
    const normalizedDefaultPayee = checkInquiryResponse?.isFirstPartyModified
      ? [
          payeeDetails?.payeeName1 ?? '',
          payeeDetails?.payeeName2 ?? '',
          payeeDetails?.payeeName3 ?? ''
        ].filter((payeeName) => !!payeeName?.trim())
      : checkInquiryResponse?.cashieringCheckInputDetails?.cashieringPayeeDetails?.defaultPayee;
    return {
      ...state,
      nickName: checkInquiryResponse.nickName,
      defaultPayee: normalizedDefaultPayee,
      defaultRecipient: checkInquiryResponse?.cashieringCheckInputDetails?.cashieringAddressDetails?.defaultRecipient,
      shouldShowStateTax: hasStateTax(
        checkInquiryResponse?.cashieringCheckInputDetails?.cashieringCheckTransaction?.nraAccountIndicator,
        checkInquiryResponse?.stwProcessCode,
        checkInquiryResponse?.stateTaxAmountFormatted
        || (checkInquiryResponse?.stwProcessCode === 'N' ? checkInquiryResponse?.stateTaxRate?.toString() : undefined)
      ),
      isThirdParty: checkInquiryResponse?.isThirdParty,
      checkReasonMap: checkReasonMap,
      loading: false,
      iraAccount: checkInquiryResponse.iraaccount,
      memo: checkInquiryResponse.memo,
      processDate: checkInquiryResponse.processDate,
      pendReasons: checkInquiryResponse.pendReasonsListSet,
      checkFee: checkInquiryResponse.checkFeeDetails.defaultFee - checkInquiryResponse.checkFeeDetails.qualFee,
      deliveryAddress: getDeliveryAddressOnInquiryCallSuccess(checkInquiryResponse.cashieringCheckInputDetails.cashieringAddressDetails.defaultRecipient,
        checkInquiryResponse.cashieringCheckInputDetails.cashieringAddressDetails.addressDetails),
      frequencyList: checkInquiryResponse.frequencyList,
      fedTaxAmountFormatted: checkInquiryResponse.fedTaxAmountFormatted,
      stateTaxAmountFormatted: checkInquiryResponse.stateTaxAmountFormatted,
      fedTaxRate: fedTax.percentage,
      stateTaxRate: stateTax.percentage,
      totalWithdrawalAmount: checkInquiryResponse.cashieringCheckInputDetails.formattedAmount,
      amount: checkInquiryResponse.formattedNetAmount,
      cashieringCheckInputDetails: {
        ...state.cashieringCheckInputDetails,
        ...checkInquiryResponse.cashieringCheckInputDetails
      },
      isFirstPartyModified: checkInquiryResponse.isFirstPartyModified,
      useConstructedPayee: checkInquiryResponse.cashieringCheckInputDetails.cashieringPayeeDetails.useConstructedPayee,
      payee: [
        payeeDetails?.payeeName1 ?? '',
        payeeDetails?.payeeName2 ?? '',
        payeeDetails?.payeeName3 ?? ''
      ],
      payeeDetails: payeeDetails,
      recipient: checkInquiryResponse.cashieringCheckInputDetails?.cashieringAddressDetails?.addressDetails,
      checkFeeDetails: checkInquiryResponse.checkFeeDetails,
      checkReason: checkReason,
      isWithholdFedTax: Number.parseFloat(fedTax.percentage) !== 0,
      isWithholdStateTax: Number.parseFloat(stateTax.percentage) !== 0,
      inputIraFields: {
        stateTaxAmountFormatted: checkInquiryResponse?.cashieringCheckInputDetails?.cashieringCheckTransaction?.stateTaxAmountFormatted,
        stateTaxPercent: (checkInquiryResponse?.cashieringCheckInputDetails?.cashieringCheckTransaction?.stateTaxAmountFormatted),
        fedTaxPercent: (checkInquiryResponse?.cashieringCheckInputDetails?.cashieringCheckTransaction?.fedTaxAmountFormatted),
      },
      requestedDate: checkInquiryResponse.cashieringCheckInputDetails.requestDate,
      requestType: getInquiryRequestType(checkInquiryResponse.type, state.requestType),
      recurringFields: {
        ...state.recurringFields,
        endDate: (checkInquiryResponse.endDateMM && checkInquiryResponse.endDateDD && checkInquiryResponse.endDateYYYY)
          ? `${checkInquiryResponse.endDateMM}-${checkInquiryResponse.endDateDD}-${checkInquiryResponse.endDateYYYY}`
          : '',
        secondProcessDate: (checkInquiryResponse.process2ndDateMM && checkInquiryResponse.process2ndDateDD && checkInquiryResponse.processDateYYYY)
          ? `${checkInquiryResponse.process2ndDateMM}-${checkInquiryResponse.process2ndDateDD}-${checkInquiryResponse.processDateYYYY}`
          : ''
      },
      cashieringRequestId: checkInquiryResponse?.cashieringReqId,
      firmDetails: checkInquiryResponse?.firmDetails,
      stateList: checkInquiryResponse.stateList,
      countryList: checkInquiryResponse.countryList,
      distributionReason: mapCheckDistributionReasonList(checkInquiryResponse),
      distributionReasonsList: checkInquiryResponse.distributionReasonsList,
      stwProcessCode: checkInquiryResponse.stwProcessCode,
      mailAddressDetails: checkInquiryResponse?.cashieringCheckInputDetails?.cashieringAddressDetails?.mailAddressDetails,
      mailAddressTypeCode: checkInquiryResponse?.cashieringCheckInputDetails?.cashieringAddressDetails?.mailAddressTypeCode,
      checkPayeeTypeCode: checkInquiryResponse?.cashieringCheckInputDetails?.cashieringPayeeDetails?.checkPayeeTypeCode,
      cashieringIraAgreementText: checkInquiryResponse?.cashieringIraAgreementText,
      isBsloaTransaction: checkInquiryResponse?.isBundledSloaUsage,
      checkNumber: checkInquiryResponse?.checkNumber,
      status: {
        code: checkInquiryResponse?.statusCode,
        description: checkInquiryResponse?.statusDescription
      },
      stateCd: checkInquiryResponse?.cashieringCheckInputDetails?.cashieringAccountRefDetails?.stateCd,
      nraAccountIndicator: checkInquiryResponse?.cashieringCheckInputDetails?.cashieringCheckTransaction?.nraAccountIndicator,
      isRmdEligible: checkInquiryResponse?.cashieringCheckInputDetails?.cashieringAccountRefDetails?.isRmdEligible,
    }
  }),
  on(ViewPageActions.backButtonClicked,
    ViewPageActions.backButtonClickedFromMsv,
    () => {
      return {
        ...initialCheckInfoState
      }
  }),
  on(ReviewPageActions.clearAttentionMessage, (state, _) => {
    return {
      ...state,
      messageInfo: {
        errorList: [],
        warningList: [],
        atsSystemErrors: []
      },
      pendReasonsListSet: []
    };
  }),
  on(ReviewPageActions.submitAndCreateToggled, (state, payload) => {
    return {
      ...state,
      isSubmitAndCreate: payload.isSubmitAndCreate,
    }
  }),
  on(ReviewPageActions.saveAsTemplateToggled, (state) => {
    return {
      ...state,
      isSubmitAndCreate: false
    }
  })
)

export function mapCheckReasonsToList(checkReasonMap: object): CodeAndDescription[] {
  let checkReasonArr: CodeAndDescription[] = [new CodeAndDescription()];
  if (checkReasonMap) {
    for (const [key, value] of Object.entries(checkReasonMap)) {
      checkReasonArr.push(new CodeAndDescription(key, value))
    }
  }
  return checkReasonArr;
}

function hasStateTax(nraIndicator: boolean, stwProcessCode: StwProcessCode, stateTaxValue?: string): boolean {
  if (nraIndicator) {
    return true;
  } else if (stwProcessCode === 'N') {
    return isZeroStateTaxValue(stateTaxValue);
  } else if (stateTaxValue?.trim()) {
    return true;
  } else if (stwProcessCode) {
    return true;
  } else {
    return false;
  }
}

function isZeroStateTaxValue(stateTaxValue?: string): boolean {
  const stateTaxDisplayValue = stateTaxValue?.trim();

  if (!stateTaxDisplayValue) {
    return false;
  }

  const { percentage } = TaxAmountAndPercentageFormatter(stateTaxDisplayValue);
  const formattedPercentage = percentage?.replace(/[^0-9.-]/g, '');
  const formattedStateTaxValue = stateTaxDisplayValue.replace(/[^0-9.-]/g, '');

  return (formattedPercentage !== '' && Number(formattedPercentage) === 0)
    || (formattedStateTaxValue !== '' && Number(formattedStateTaxValue) === 0);
}

function mapCheckDistributionReasonList(checkInputResponse: CheckData): DistributionReason;

function mapCheckDistributionReasonList(checkInquiryResponse: CheckInquiryResponse): DistributionReason;

function mapCheckDistributionReasonList(checkDetails: any): any {
  let distributionReasonCode = checkDetails.distributionReasonCode;
  let distributionReasonCodeSubcode = checkDetails.distributionReasonCodeSubcode.split('-')[1];

  return checkDetails.distributionReasonsList.find((reason: any) => reason.code === distributionReasonCode && reason.subCode.trim() === distributionReasonCodeSubcode.trim()) ?? new DistributionReason();
}

export function showWarningText(distributionReason: DistributionReason, distributionReasonList: DistributionReason[], classifications: Classifications) {
  let selectedReason = distributionReasonList?.filter(
    (element) =>
      element?.description === distributionReason?.description
  );
  let under59SelectionPossible = selectedReason?.some(
    (element) =>
      element.subCode === RothConversion.DistributionReasons.CONVERSION_SUB_CODE &&
      element.code.trim() ===
      RothConversion.DistributionReasons.UNDER_59_AND_A_HALF_CONVERSION_CODE
  );
  let CombineAccountPossible = selectedReason?.some(
    (element) =>
      element.subCode === RothConversion.DistributionReasons.COMBINATION_SUB_CODE &&
      element.code.trim() ===
      RothConversion.DistributionReasons.COMBINATION_CONVERSION_CODE
  );

  let isQualifiedDistribution = selectedReason?.some(
    (element) =>
      element.code.trim() ===
      RothConversion.DistributionReasons.QUALIFIED_CODE
  );

  let isQcdDistribution = selectedReason?.some(
    (element) =>
      element.subCode === RothConversion.DistributionReasons.QCD_SUB_CODE
  );

  if (under59SelectionPossible && !classifications.isCustomer59OrOlder) {
    return RothWarnings.under59InputWarning;
  } else if (CombineAccountPossible) {
    return RothWarnings.Comb0InputWarning
  } else if (isQualifiedDistribution || isQcdDistribution) {
    return RothWarnings.QualifiedDistributionWarning
  } else {
    return '';
  }
}

function getPayeeNameForNonVerifyCallFlow(details: CheckTransactionDetails): string[] {
  let payees: string[] = [details.payeeName!.toUpperCase()];
  if (details.modifiedPayeeName1) {
    payees.push(details.modifiedPayeeName1.toUpperCase());
    if (details.modifiedPayeeName2) {
      payees.push(details.modifiedPayeeName2.toUpperCase());
    }
    return payees;
  }
  if (details.additionalPayeeName1) {
    payees.push(details.additionalPayeeName1.toUpperCase());
  }
  if (details.additionalPayeeName2) {
    payees.push(details.additionalPayeeName2.toUpperCase());
  }
  return payees;
}

function getDefaultPayeeNamesForVerifyCallInRFlow(details: DraftListAttributes) {
  let defaultPayees: string[] = [];
  if (details.draftObject?.defaultPayee && details.draftObject.defaultPayee.length > 0) {
    if (details?.draftObject?.payeeDetails?.payeeName1) {
      defaultPayees.push(details?.draftObject?.payeeDetails?.payeeName1.toUpperCase() ?? '');
    }
    if (details?.draftObject?.payeeDetails?.payeeName2) {
      defaultPayees.push(details?.draftObject?.payeeDetails?.payeeName2.toUpperCase() ?? '');
    }
    if (details?.draftObject?.payeeDetails?.payeeName3) {
      defaultPayees.push(details?.draftObject?.payeeDetails?.payeeName3.toUpperCase() ?? '');
    }
    return defaultPayees;
  }
  else if (details?.draftObject?.defaultPayee?.length == 0) {
    defaultPayees.push(details?.draftObject?.defaultPayee[0].toUpperCase() ?? '');
  }
  return defaultPayees;
}

function getDeliveryAddressForNonVerifyCallFlow(details: CheckTransactionDetails, countryList: CodeAndDescription[]): string[] {
  let deliveryAddress: string[] = [];
  deliveryAddress.push(details.addresseeName?.toUpperCase() ?? '');
  if (details.addresseeNameCont) {
    deliveryAddress.push(details.addresseeNameCont.toUpperCase());
  }
  if (details.addresseeNameCont2) {
    deliveryAddress.push(details.addresseeNameCont2.toUpperCase());
  }
  deliveryAddress.push(details.streetAddress?.toUpperCase() ?? '');
  if (details.streetAddressCont) {
    deliveryAddress.push(details.streetAddressCont.toUpperCase());
  }
  if (details.country) {
    deliveryAddress.push(`${details.city}, ${details.stateProvince ?? ''} ${details.postalCode}`.toUpperCase());
    deliveryAddress.push(countryList.find(country => country.code === details.country!.toUpperCase())!.description);
  } else {
    deliveryAddress.push(`${details.city}, ${details.state} ${details.zipCode}`.toUpperCase());
  }
  return deliveryAddress;
}

function getDeliveryAddressOnInquiryCallSuccess(defaultRecipient: string[], addressDetails: RecipientAddressDetails) {
  let deliveryAddress = [...defaultRecipient]
  if (addressDetails?.zipSuffCode.trim()) {
    let lastIndex = deliveryAddress.length - 1;
    let zipWithSuffCode = deliveryAddress[lastIndex] + ` - ${addressDetails.zipSuffCode}`;
    deliveryAddress[lastIndex] = zipWithSuffCode;
  }

  return deliveryAddress;
}

/**
 * @description A function that takes a string like "$11.12 cleared on 04-03-2023" and returns "$11.12 cleared on 04/03/2023"
 * @param fundsHeld An array of strings that are formatted like the above
 * @returns A new array with the same information but a formatted date
 */
function mapFundsHeld(fundsHeld: string[]): string[] {
  let newArr = [];
  for (const elem of fundsHeld) {
    let parts = elem.split(' ');
    let date = parts[3];
    if (date && DateTime.fromFormat(date, 'MM-dd-yyyy').isValid) {
      let formatted = DateTime.fromFormat(date, 'MM-dd-yyyy').toFormat('MM/dd/yyyy');
      newArr.push(parts.slice(0, 3).join(' ') + ' ' + formatted)
    } else {
      newArr.push(parts.join(' '));
    }
  }
  return newArr;
}

function UpdateEauthCustomers(eAuthCustomerList: EAuthModelCustomer[], info: AccountInfoResponse): EAuthModelCustomer[] {
  let list: EAuthModelCustomer[] = [];
  eAuthCustomerList.forEach(item => {
    let update = info.eauthorization.customers.find(customer => {
      return customer.customerId === item.customerId
    })
    if (update) {
      let result: EAuthModelCustomer =
      {
        isAccountWebEnabled: update.isAccountWebEnabled,
        customerWebEnrolled: "false",
        customerId: update.customerId,
        isCustomerWebEnrolled: update.isCustomerWebEnrolled,
        emailId: update.emailId,
        firstName: update.firstName,
        lastName: update.lastName,
        loginIdEnrollStatus: update.loginIdEnrollmentStatus,
        isDisplayAllowed: update.displayIndicator,
        isCustomerEAuthEligible: item.isCustomerEAuthEligible,
        organizationName: update.organizationName,
        eauthInd: item.eauthInd,
        typeCode: update.typeCode,
        customerRestrictionReason: item.customerRestrictionReason,
        accountRoles: update.accountRoles,
        eligibility: item.eligibility
      }
      list.push(result);
    } else {
      list.push(item);
    }
  });
  return list;
}

function getConcatenateDeliveryAddress(response: DraftListAttributes) {
  let deliveryAddress: string[] = [];
  deliveryAddress.push(response.draftObject.otherDomesticmailTo1 ?? '');
  deliveryAddress.push(response.draftObject.otherDomesticmailTo2 ?? '');
  deliveryAddress.push(response.draftObject.otherDomesticmailTo3 ?? '');
  deliveryAddress.push(response.draftObject.otherDomesticstreetAddr1 ?? '');
  deliveryAddress.push(response.draftObject.otherDomesticstreetAddr2 ?? '');
  let domesticAddress = response.draftObject.otherDomesticAddrCity ?? '';
  domesticAddress = concatenateStrings(domesticAddress, response.draftObject.otherDomesticAddrState ?? '');
  deliveryAddress.push(`${domesticAddress ?? ''} ${response.draftObject.otherDomesticAddrZipCode ?? ''}`);
  deliveryAddress.push(response.draftObject.otherForeginmailTo1 ?? '');
  deliveryAddress.push(response.draftObject.otherForeginmailTo2 ?? '');
  deliveryAddress.push(response.draftObject.otherForeginmailTo3 ?? '');
  deliveryAddress.push(response.draftObject.otherForeginstreetAddr1 ?? '');
  deliveryAddress.push(response.draftObject.otherForeginstreetAddr2 ?? '');
  let foreignAddress = response.draftObject.otherForeginAddrCity ?? '';
  foreignAddress = concatenateStrings(foreignAddress, response.draftObject.otherForeginAddrState ?? '');
  deliveryAddress.push(`${foreignAddress ?? ''} ${response.draftObject.otherForegincAddrZipCode ?? ''}`);
  deliveryAddress.push(response.draftObject.otherForeginAddrCountry ?? '');

  return deliveryAddress;
}

function concatenateStrings(existingVal: string, newVal: string): string {
  if (newVal) {
    return existingVal ? `${existingVal}, ${newVal}` : newVal;
  }
  return existingVal;
}

function getInquiryRequestType(inquiryRequestType?: string, existingRequestType?: string): string {
  if (!inquiryRequestType) {
    return existingRequestType ?? '';
  }

  // Inquiry can return recurringCheck for edited transactions. We normalize to onetimeCheck
  // to keep request type handling aligned with the standard input flow behavior.
  if (inquiryRequestType === 'recurringCheck') {
    return 'onetimeCheck';
  }

  return inquiryRequestType;
}

